import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check, ArrowRight, Star, Lightning, GearSix } from "@phosphor-icons/react";
import { devWarn } from "../lib/logger";

const FALLBACK_PLANS = [
  { key: "standard", name: "ETA Build Standard", amount_eur: 8, features: [], description: "" },
  { key: "premium", name: "ETA Build Premium", amount_eur: 15, features: [], description: "" },
];

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(null); // "standard" | "premium" | null
  const [portalLoading, setPortalLoading] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [trialDays, setTrialDays] = useState(30);
  const nav = useNavigate();

  // Load catalogue
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscriptions/plans");
        setPlans(data.plans || FALLBACK_PLANS);
        setTrialDays(data.trial_days || 30);
      } catch (e) { devWarn("plans fetch failed", e); }
    })();
  }, []);

  // Poll after Stripe redirect
  const pollStatus = useCallback(async () => {
    if (!sessionId) return;
    let attempts = 0;
    setPolling(true);
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscriptions/checkout/status/${sessionId}`);
        if (data.granted || data.subscription_status === "trialing" || data.subscription_status === "active") {
          toast.success("Abbonamento attivato!");
          await refresh();
          setPolling(false);
          return;
        }
        if (data.status === "expired" || attempts > 10) {
          toast.error("Pagamento non completato");
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        setPolling(false);
      }
    };
    tick();
  }, [sessionId, refresh]);

  useEffect(() => { pollStatus(); }, [pollStatus]);

  const subscribe = async (planKey) => {
    setLoading(planKey);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan: planKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
      setLoading(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/subscriptions/portal", {
        return_url: `${window.location.origin}/abbonamento`,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore portale clienti");
      setPortalLoading(false);
    }
  };

  // Admin override card
  if (user?.is_admin) {
    return (
      <Layout>
        <Header />
        <div className="bg-[#FFD600]/10 border-2 border-[#FFD600] rounded-md p-6 text-center" data-testid="admin-card">
          <div className="font-heading text-3xl font-black text-[#FFD600] uppercase">Account Admin</div>
          <div className="text-sm text-white mt-2">Accesso completo gratuito a vita</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header />

      {polling && (
        <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-md p-4 mb-4 text-center" data-testid="polling">
          <div className="font-bold uppercase text-[#FFD600]">Verifica pagamento…</div>
        </div>
      )}

      {user?.subscription_status === "active" && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4" data-testid="active-banner">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-heading text-xl font-black text-[#FFD600] uppercase">Abbonamento Attivo</div>
              {user?.subscription_active_until && (
                <div className="text-xs text-[#A3A3A3] mt-1">
                  Rinnovo / fine periodo: {new Date(user.subscription_active_until).toLocaleDateString("it-IT")}
                </div>
              )}
              {user?.stripe_plan && (
                <div className="text-xs text-[#A3A3A3] mt-0.5 uppercase tracking-wider">Piano: {user.stripe_plan}</div>
              )}
            </div>
            <button onClick={openPortal} disabled={portalLoading} data-testid="portal-btn"
              className="bg-[#FFD600] text-[#0A0A0A] h-10 px-3 rounded-md font-black uppercase text-xs flex items-center gap-1 disabled:opacity-50">
              <GearSix size={16} weight="bold"/> Gestisci
            </button>
          </div>
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.2em] text-[#FFD600] font-bold mb-3">
        {trialDays} giorni di prova gratuita
      </div>

      <div className="grid grid-cols-1 gap-4">
        {plans.map((p) => (
          <PlanCard
            key={p.key}
            plan={p}
            trialDays={trialDays}
            highlight={p.key === "premium"}
            loading={loading === p.key}
            disabled={!!loading}
            onSubscribe={() => subscribe(p.key)}
          />
        ))}
      </div>

      <div className="mt-4 text-xs text-[#666] text-center">
        Il campo per i codici promo è disponibile direttamente in Stripe Checkout.
      </div>

      <button onClick={()=>nav("/dashboard")} className="w-full mt-3 text-[#A3A3A3] text-sm font-bold uppercase tracking-wider" data-testid="back-dashboard">
        Torna alla dashboard
      </button>
    </Layout>
  );
}

function Header() {
  return (
    <div className="mb-6">
      <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Piano</div>
      <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">Abbonamento</h1>
    </div>
  );
}

function PlanCard({ plan, trialDays, highlight, loading, disabled, onSubscribe }) {
  const Icon = plan.key === "premium" ? Star : Lightning;
  const accent = highlight ? "border-[#FFD600] bg-gradient-to-br from-[#FFD600]/15 to-[#FFD600]/0" : "border-[#333] bg-[#1A1A1A]";
  return (
    <div className={`rounded-md border-2 p-5 ${accent}`} data-testid={`plan-card-${plan.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[#FFD600]">
          <Icon size={20} weight={highlight ? "fill" : "bold"} />
          <span className="text-xs uppercase tracking-wider font-bold">{plan.key === "premium" ? "Premium" : "Standard"}</span>
        </div>
        {highlight && (
          <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded bg-[#FFD600] text-[#0A0A0A]">Top</span>
        )}
      </div>

      <div className="font-heading text-5xl font-black tracking-tighter">
        {Number(plan.amount_eur).toFixed(0)}<span className="text-2xl">€</span>
        <span className="text-base text-[#A3A3A3] font-bold">/mese</span>
      </div>
      <div className="text-xs text-[#A3A3A3] mt-1">IVA inclusa · Prima {trialDays} giorni gratis</div>

      <ul className="mt-4 space-y-2">
        {(plan.features || []).map((t) => (
          <li key={t} className="flex items-start gap-2">
            <Check size={18} weight="bold" className="text-[#FFD600] mt-0.5 shrink-0"/>
            <span className="text-sm text-white">{t}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`subscribe-${plan.key}-btn`}
        onClick={onSubscribe}
        disabled={disabled}
        className={`mt-5 w-full h-14 font-black uppercase rounded-md disabled:opacity-50 flex items-center justify-center gap-2 ${
          highlight ? "bg-[#FFD600] text-[#0A0A0A] hover:bg-[#E6C200]" : "bg-[#FFD600] text-[#0A0A0A] hover:bg-[#E6C200]"
        }`}
      >
        {loading ? "..." : (<>Inizia ora <ArrowRight size={20} weight="bold"/></>)}
      </button>
    </div>
  );
}
